import React, { useState, useEffect, useRef } from 'react';
import { DashboardLayout } from '../../components/ems/DashboardLayout';
import { usersApi, exportApi } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { 
  Plus,
  Search,
  MoreVertical,
  Upload,
  Download,
  UserPlus,
  Edit,
  Trash2,
  Shield,
  Users,
  Info,
  Radio,
  Zap,
} from 'lucide-react';

const ROLE_LABELS = { hub_admin: 'Hub Admin', super_admin: 'Super Admin', simcel: 'Simcell', observer: 'Observer', participant: 'Participant', controller: 'Controller' };
const formatRole = (role) => ROLE_LABELS[role] || role.replace('_', ' ');

const EMS_ROLES = [
  { 
    value: 'hub_admin', 
    label: 'Hub Admin',
    description: 'Full access to both EMS and Media Portal. Can manage all users and system-wide settings from the Hub.',
    color: 'text-amber-700 dark:text-amber-400',
  },
  { 
    value: 'super_admin', 
    label: 'Super Admin',
    description: 'Full EMS access. Can manage all users, exercises, settings, branding, and view all data.',
    color: 'text-blue-700 dark:text-blue-400',
  },
  { 
    value: 'simcel', 
    label: 'Simcell',
    description: 'Exercise controller. Can create/manage exercises, trigger injects, view responses, and control exercise flow.',
    color: 'text-purple-700 dark:text-purple-400',
  },
  { 
    value: 'observer', 
    label: 'Observer',
    description: 'Read-only access to assigned exercises. Can view injects, responses, map, and custom tabs.',
    color: 'text-teal-700 dark:text-teal-400',
  },
  { 
    value: 'participant', 
    label: 'Participant',
    description: 'Phone-only interface. Can access the softphone and view assigned exercise communications.',
    color: 'text-foreground dark:text-muted-foreground',
  },
];

// V14.10 — Media Portal roles unified 1:1 with EMS (super_admin / simcel /
// observer / participant). Backend `mp/shared.py` auto-derives MP role from
// the EMS role on every request, so these labels mirror EMS_ROLES (minus
// hub_admin which is Hub-only). Legacy values (admin/author/viewer) are
// normalised at edit-time below.
const MP_ROLES = [
  {
    value: 'super_admin',
    label: 'Super Admin',
    description: 'Full Media Portal access — manage exercises, posts, platforms.',
    color: 'text-red-700 dark:text-red-400',
  },
  {
    value: 'simcel',
    label: 'Simcell',
    description: 'Author / moderate content for assigned exercises.',
    color: 'text-amber-700 dark:text-amber-400',
  },
  {
    value: 'observer',
    label: 'Observer',
    description: 'Read-only access to assigned MP exercises.',
    color: 'text-emerald-700 dark:text-emerald-400',
  },
  {
    value: 'participant',
    label: 'Participant',
    description: 'Live Display kiosk view only.',
    color: 'text-purple-700 dark:text-purple-400',
  },
];

// Normalise legacy MP role IDs stored in old user docs
const normalizeMpRoles = (roles) => {
  const map = { admin: 'super_admin', author: 'simcel', viewer: 'participant', user: 'participant' };
  return (roles || []).map(r => map[r] || r);
};

const ALL_ROLES_FLAT = [...EMS_ROLES, ...MP_ROLES.map(r => ({ ...r, value: `mp:${r.value}` }))];

export default function UsersPage() {
  const { user: currentUser, hasRole } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    roles: ['participant'],
    mp_roles: [],
  });
  
  const fileInputRef = useRef(null);

  useEffect(() => { loadUsers(); }, []);

  const loadUsers = async () => {
    try {
      const data = await usersApi.getAll();
      setUsers(data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    try {
      await usersApi.create({
        email: formData.email,
        name: formData.name,
        password: formData.password,
        roles: formData.roles,
        mp_roles: formData.mp_roles,
      });
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      setFormData({ name: '', email: '', password: '', roles: ['participant'], mp_roles: [] });
      loadUsers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!selectedUser) return;
    setFormLoading(true);
    try {
      const updateData = {
        name: formData.name,
        roles: formData.roles,
        mp_roles: formData.mp_roles,
        status: formData.status,
      };
      if (formData.email && formData.email !== selectedUser.email) {
        updateData.email = formData.email;
      }
      if (formData.password) {
        updateData.password = formData.password;
      }
      await usersApi.update(selectedUser.user_id, updateData);
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await usersApi.delete(userId);
      toast.success('User deleted');
      loadUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleBulkImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormLoading(true);
    try {
      const result = await usersApi.bulkImport(file);
      toast.success(`Imported ${result.created} users`);
      if (result.errors?.length > 0) toast.warning(`${result.errors.length} rows had errors`);
      setImportDialogOpen(false);
      loadUsers();
    } catch (error) {
      toast.error(error.message);
    } finally {
      setFormLoading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleExportUsers = async () => {
    try {
      const blob = await exportApi.usersCsv();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'users_export.csv';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch (error) {
      toast.error('Export failed');
    }
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      roles: user.roles || [],
      mp_roles: normalizeMpRoles(user.mp_roles),
      status: user.status,
    });
    setEditDialogOpen(true);
  };

  const handleRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      roles: prev.roles.includes(role) ? prev.roles.filter(r => r !== role) : [...prev.roles, role],
    }));
  };

  const handleMpRoleToggle = (role) => {
    setFormData(prev => ({
      ...prev,
      mp_roles: prev.mp_roles.includes(role) ? prev.mp_roles.filter(r => r !== role) : [...prev.mp_roles, role],
    }));
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadgeStyle = (role, isMP = false) => {
    if (isMP) {
      const styles = { admin: 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20', author: 'bg-pink-100 text-pink-700 border-pink-300 dark:bg-pink-500/10 dark:text-pink-400 dark:border-pink-500/20', user: 'bg-muted text-foreground border-border dark:bg-muted0/10 dark:text-muted-foreground dark:border-border/20' };
      return styles[role] || '';
    }
    const styles = { hub_admin: 'bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-500/10 dark:text-amber-400 dark:border-amber-500/20', super_admin: 'bg-blue-100 text-blue-700 border-blue-300 dark:bg-blue-500/10 dark:text-blue-400 dark:border-blue-500/20', simcel: 'bg-purple-100 text-purple-700 border-purple-300 dark:bg-purple-500/10 dark:text-purple-400 dark:border-purple-500/20', observer: 'bg-teal-100 text-teal-700 border-teal-300 dark:bg-teal-500/10 dark:text-teal-400 dark:border-teal-500/20', participant: 'bg-muted text-foreground border-border dark:bg-muted0/10 dark:text-muted-foreground dark:border-border/20' };
    return styles[role] || '';
  };

  // Role section component for dialogs
  const RoleSectionEMS = ({ isEdit = false }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Shield className="w-4 h-4 text-blue-500" />
        <Label className="text-sm font-medium">EMS Roles</Label>
      </div>
      <div className="grid grid-cols-1 gap-1.5 p-3 rounded-lg border bg-muted/30">
        <TooltipProvider>
          {EMS_ROLES.map((role) => (
            <div key={role.value} className="flex items-center space-x-2 py-1">
              <Checkbox
                id={`${isEdit ? 'edit-' : ''}ems-role-${role.value}`}
                checked={formData.roles.includes(role.value)}
                onCheckedChange={() => handleRoleToggle(role.value)}
                disabled={role.value === 'hub_admin' && !hasRole(['hub_admin'])}
                data-testid={`${isEdit ? 'edit-' : ''}ems-role-${role.value}`}
              />
              <Label htmlFor={`${isEdit ? 'edit-' : ''}ems-role-${role.value}`} className="text-sm flex items-center gap-1.5 cursor-pointer">
                <span className={role.color}>{role.label}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[220px]">
                    <p className="text-xs">{role.description}</p>
                  </TooltipContent>
                </Tooltip>
              </Label>
            </div>
          ))}
        </TooltipProvider>
      </div>
    </div>
  );

  const RoleSectionMP = ({ isEdit = false }) => (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Radio className="w-4 h-4 text-purple-500" />
        <Label className="text-sm font-medium">Media Portal Roles</Label>
      </div>
      <div className="grid grid-cols-1 gap-1.5 p-3 rounded-lg border bg-muted/20">
        <TooltipProvider>
          {MP_ROLES.map((role) => (
            <div key={role.value} className="flex items-center space-x-2 py-1">
              <Checkbox
                id={`${isEdit ? 'edit-' : ''}mp-role-${role.value}`}
                checked={formData.mp_roles.includes(role.value)}
                onCheckedChange={() => handleMpRoleToggle(role.value)}
                data-testid={`${isEdit ? 'edit-' : ''}mp-role-${role.value}`}
              />
              <Label htmlFor={`${isEdit ? 'edit-' : ''}mp-role-${role.value}`} className="text-sm flex items-center gap-1.5 cursor-pointer">
                <span className={role.color}>{role.label}</span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-3 h-3 text-muted-foreground cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent side="right" className="max-w-[220px]">
                    <p className="text-xs">{role.description}</p>
                  </TooltipContent>
                </Tooltip>
              </Label>
            </div>
          ))}
        </TooltipProvider>
      </div>
    </div>
  );

  return (
    <DashboardLayout title="Users">
      <div className="space-y-6" data-testid="users-page">
        {/* Header Actions */}
        <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="relative flex-1 sm:max-w-xs w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search users..."
              className="pl-10"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              data-testid="search-users"
            />
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExportUsers}>
              <Download className="w-4 h-4 mr-2" /> Export
            </Button>
            <Button variant="outline" onClick={() => setImportDialogOpen(true)}>
              <Upload className="w-4 h-4 mr-2" /> Import
            </Button>
            <Button onClick={() => { setFormData({ name: '', email: '', password: '', roles: ['participant'], mp_roles: [] }); setCreateDialogOpen(true); }} data-testid="create-user-btn">
              <UserPlus className="w-4 h-4 mr-2" /> Add User
            </Button>
          </div>
        </div>

        {/* Users Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="py-12 text-center">
                <Users className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-lg font-medium">No users found</p>
                <p className="text-muted-foreground">
                  {searchQuery ? 'Try a different search term' : 'Add your first user to get started'}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>User</TableHead>
                    <TableHead>EMS Roles</TableHead>
                    <TableHead>MP Roles</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Created</TableHead>
                    <TableHead className="w-[50px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredUsers.map((u) => (
                    <TableRow key={u.user_id} data-testid={`user-row-${u.user_id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                            {u.picture ? (
                              <img src={u.picture} alt="" className="w-9 h-9 rounded-full" />
                            ) : (
                              <span className="font-semibold text-primary text-sm">
                                {u.name?.charAt(0).toUpperCase()}
                              </span>
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-sm">{u.name}</p>
                            <p className="text-xs text-muted-foreground">{u.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {u.roles?.map((role) => (
                            <span
                              key={role}
                              className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getRoleBadgeStyle(role)}`}
                            >
                              {role === 'hub_admin' && <Zap className="w-3 h-3 mr-0.5" />}
                              {formatRole(role)}
                            </span>
                          ))}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1">
                          {normalizeMpRoles(u.mp_roles).length > 0 ? (
                            normalizeMpRoles(u.mp_roles).map((role) => {
                              const config = MP_ROLES.find(r => r.value === role);
                              return (
                                <span
                                  key={role}
                                  className={`inline-flex items-center px-2 py-0.5 rounded text-[11px] font-medium border ${getRoleBadgeStyle(role, true)}`}
                                >
                                  <Radio className="w-3 h-3 mr-0.5" />
                                  {config?.label || role}
                                </span>
                              );
                            })
                          ) : (
                            <span className="text-xs text-muted-foreground">—</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge 
                          variant={u.status === 'active' ? 'default' : 'secondary'}
                          className="capitalize text-xs"
                        >
                          {u.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {u.created_at && new Date(u.created_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`user-actions-${u.user_id}`}>
                              <MoreVertical className="w-4 h-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEditDialog(u)}>
                              <Edit className="w-4 h-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            {u.user_id !== currentUser?.user_id && (
                              <DropdownMenuItem 
                                className="text-destructive"
                                onClick={() => handleDeleteUser(u.user_id)}
                              >
                                <Trash2 className="w-4 h-4 mr-2" /> Delete
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-secondary">Create User</DialogTitle>
              <DialogDescription>Add a new user with EMS and/or Media Portal access</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="name">Full Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    required
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email">Email *</Label>
                  <Input
                    id="email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    required
                    data-testid="user-email-input"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password *</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  required
                  minLength={6}
                  data-testid="user-password-input"
                />
              </div>

              {/* Role Sections Side by Side */}
              <div className="grid grid-cols-2 gap-3">
                <RoleSectionEMS />
                <RoleSectionMP />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading} data-testid="submit-user-btn">
                  {formLoading ? 'Creating...' : 'Create User'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-secondary">Edit User</DialogTitle>
              <DialogDescription>Update user details and permissions for both platforms</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-name">Full Name</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    data-testid="edit-user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-email">Email</Label>
                  <Input
                    id="edit-email"
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    data-testid="edit-user-email-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="edit-password">New Password (leave blank to keep)</Label>
                  <Input
                    id="edit-password"
                    type="password"
                    value={formData.password}
                    placeholder="••••••••"
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    data-testid="edit-user-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select 
                    value={formData.status}
                    onValueChange={(v) => setFormData({...formData, status: v})}
                  >
                    <SelectTrigger data-testid="edit-user-status">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Active</SelectItem>
                      <SelectItem value="inactive">Inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Role Sections Side by Side */}
              <div className="grid grid-cols-2 gap-3">
                <RoleSectionEMS isEdit />
                <RoleSectionMP isEdit />
              </div>

              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={formLoading} data-testid="edit-submit-btn">
                  {formLoading ? 'Saving...' : 'Save Changes'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>

        {/* Import Dialog */}
        <Dialog open={importDialogOpen} onOpenChange={setImportDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-secondary">Import Users</DialogTitle>
              <DialogDescription>
                Upload a CSV or Excel (.xlsx) file with user data. Required column: <b>email</b>.
                Optional columns: <b>name</b>, <b>roles</b> (comma-separated), <b>password</b>, <b>organisation_id</b>. Existing users (matched by email) are skipped.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="border-2 border-dashed border-border rounded-lg p-8 text-center">
                <Upload className="w-12 h-12 mx-auto text-muted-foreground mb-4" />
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.xlsx,.xlsm"
                  onChange={handleBulkImport}
                  className="max-w-xs mx-auto"
                  data-testid="users-bulk-import-input"
                />
                <p className="text-sm text-muted-foreground mt-2">
                  CSV or Excel — columns: email, name, roles, password
                </p>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
